import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getEscrows,
  resolveEscrowDispute,
  getEscrowDetail
} from '../../services/api';
import { 
  FiSearch, 
  FiFilter, 
  FiRefreshCw, 
  FiEye, 
  FiCheckCircle, 
  FiXCircle, 
  FiArrowLeft, 
  FiArrowRight,
  FiDownload,
  FiAlertCircle,
  FiAlertTriangle,
  FiMessageSquare,
  FiFileText,
  FiUsers,
  FiCalendar,
  FiClock,
  FiTrendingUp,
  FiTrendingDown,
  FiPercent,
  FiCheck,
  FiX,
  FiDollarSign,
  FiGitPullRequest,
  FiShield,
  FiBarChart2,
  FiActivity
} from 'react-icons/fi';

function EscrowDisputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    status: 'disputed',
    search: '',
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [resolutionModal, setResolutionModal] = useState(false);
  const [resolutionData, setResolutionData] = useState({
    resolution: 'release_seller',
    refund_amount: 0,
    release_amount: 0,
    resolution_notes: ''
  });

  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    fetchDisputes();
  }, [filters, pagination.page]);

  const fetchDisputes = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await getEscrows(params);
      if (response.success) {
        // Filter to only disputed escrows
        const disputedEscrows = response.data.escrows?.filter(e => e.dispute_id) || [];
        setDisputes(disputedEscrows);
        setPagination(prev => ({
          ...prev,
          total: disputedEscrows.length,
          pages: Math.ceil(disputedEscrows.length / pagination.limit)
        }));
      } else {
        setError(response.message || 'Failed to load disputes');
      }
    } catch (err) {
      setError('Failed to load disputes: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleResolveDispute = async () => {
    try {
      if (!selectedDispute) return;

      const response = await resolveEscrowDispute(
        selectedDispute.escrow_id,
        resolutionData.resolution,
        resolutionData.refund_amount,
        resolutionData.release_amount,
        resolutionData.resolution_notes
      );

      if (response.success) {
        setSuccessMessage(`Dispute resolved successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setResolutionModal(false);
        setSelectedDispute(null);
        fetchDisputes(); // Refresh list
      } else {
        throw new Error(response.message || 'Resolution failed');
      }
    } catch (err) {
      setError('Failed to resolve dispute: ' + err.message);
    }
  };

  const getDisputeBadge = (dispute) => {
    const baseClasses = "px-3 py-1 text-xs font-medium rounded-full flex items-center justify-center w-28";
    
    const statusConfig = {
      'pending': { 
        bg: 'bg-yellow-900/30', 
        text: 'text-yellow-400 border-yellow-800/50', 
        label: 'Pending', 
        icon: <FiClock className="mr-1" /> 
      },
      'in_review': { 
        bg: 'bg-blue-900/30', 
        text: 'text-blue-400 border-blue-800/50', 
        label: 'In Review', 
        icon: <FiAlertTriangle className="mr-1" /> 
      },
      'resolved': { 
        bg: 'bg-green-900/30', 
        text: 'text-green-400 border-green-800/50', 
        label: 'Resolved', 
        icon: <FiCheck className="mr-1" /> 
      }
    };

    const config = statusConfig[dispute.dispute_status] || { 
      bg: 'bg-gray-700', 
      text: 'text-gray-400', 
      label: dispute.dispute_status, 
      icon: <FiAlertTriangle className="mr-1" /> 
    };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text} border ${config.border || 'border-transparent'}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const getDisputeCategoryBadge = (category) => {
    const baseClasses = "px-2 py-0.5 text-xs font-medium rounded-full";
    
    const categoryConfig = {
      'item_not_received': { bg: 'bg-red-900/30', text: 'text-red-400', label: 'Item Not Received' },
      'item_damaged': { bg: 'bg-orange-900/30', text: 'text-orange-400', label: 'Item Damaged' },
      'not_as_described': { bg: 'bg-purple-900/30', text: 'text-purple-400', label: 'Not As Described' },
      'unauthorized_transaction': { bg: 'bg-pink-900/30', text: 'text-pink-400', label: 'Unauthorized' },
      'other': { bg: 'bg-gray-700', text: 'text-gray-400', label: 'Other' }
    };

    const config = categoryConfig[category] || { bg: 'bg-gray-700', text: 'text-gray-400', label: category };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const clearFilters = () => {
    setFilters({
      status: 'disputed',
      search: '',
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Loading Skeleton
  if (loading && disputes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-8"></div>
          
          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="p-6 border-b border-gray-700">
                <div className="flex justify-between items-center">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-48"></div>
                    <div className="h-3 bg-gray-700 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gray-700 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Escrow</span> Dispute Management
            </h1>
            <p className="text-gray-400 mt-1">Review and resolve disputed escrow transactions for buyer and seller protection</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/escrows/statistics')}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-[#ff660e] transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiBarChart2 className="mr-2" />
              Statistics
            </button>
            <button
              onClick={fetchDisputes}
              className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02] shadow-lg"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Active Disputes</p>
              <p className="text-2xl font-bold text-[#ff660e] mt-1 group-hover:text-orange-400 transition-colors">{disputes.length}</p>
              <p className="text-sm text-gray-500 mt-1">Requiring attention</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-[#ff660e]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiAlertTriangle className="text-[#ff660e] text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Amount in Dispute</p>
              <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">
                {formatCurrency(disputes.reduce((sum, d) => sum + (d.amount || 0), 0))}
              </p>
              <p className="text-sm text-gray-500 mt-1">Frozen funds</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiDollarSign className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1 group-hover:text-yellow-300 transition-colors">
                {disputes.filter(d => d.dispute_status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500 mt-1">Awaiting review</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-2xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">In Review</p>
              <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">
                {disputes.filter(d => d.dispute_status === 'in_review').length}
              </p>
              <p className="text-sm text-gray-500 mt-1">Currently reviewing</p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiActivity className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Dispute ID, listing, users, category..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                  />
                </div>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={fetchDisputes}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Disputes Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Dispute Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Parties
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Amount & Category
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Timeline
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {disputes.map((dispute) => (
                <tr key={dispute.escrow_id} className="hover:bg-gray-750 transition-colors duration-150 group">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        {getDisputeBadge(dispute)}
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded">
                          #{dispute.dispute_id}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-white truncate max-w-xs group-hover:text-[#ff660e] transition-colors">
                        {dispute.listing_title || 'Chat Escrow'}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {getDisputeCategoryBadge(dispute.dispute_category)}
                      </div>
                      {dispute.dispute_description && (
                        <div className="text-sm text-gray-400 mt-2 line-clamp-2">
                          {dispute.dispute_description}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1 space-y-1">
                        <div>Escrow ID: {dispute.escrow_id}</div>
                        {dispute.order_id && <div>Order: #{dispute.order_id}</div>}
                        {dispute.conversation_id && <div>Chat: #{dispute.conversation_id}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-3">
                      {/* Raised By */}
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Raised By</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center text-white text-xs font-bold">
                            {dispute.raised_by_first_name?.charAt(0)}{dispute.raised_by_last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white group-hover:text-[#ff660e] transition-colors">
                              {dispute.raised_by_first_name} {dispute.raised_by_last_name}
                            </div>
                            <div className="text-xs text-gray-400">
                              ID: {dispute.raised_by_id}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Against */}
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Against</div>
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                            {dispute.raised_against_first_name?.charAt(0)}{dispute.raised_against_last_name?.charAt(0)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-white group-hover:text-[#ff660e] transition-colors">
                              {dispute.raised_against_first_name} {dispute.raised_against_last_name}
                            </div>
                            <div className="text-xs text-gray-400">
                              ID: {dispute.raised_against_id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="text-2xl font-bold text-blue-400 group-hover:text-blue-300 transition-colors">
                        {formatCurrency(dispute.amount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Frozen in escrow
                      </div>
                      <div className="mt-3">
                        <div className="text-xs text-gray-400 mb-1">Evidence Files</div>
                        <div className="flex items-center gap-2">
                          <div className="px-2 py-1 bg-gray-700 text-gray-300 text-xs font-medium rounded flex items-center">
                            <FiFileText className="mr-1" />
                            {dispute.evidence_count || 0} files
                          </div>
                          {dispute.evidence_count > 0 && (
                            <button
                              onClick={() => navigate(`/admin/escrows/${dispute.escrow_id}#evidence`)}
                              className="text-[#ff660e] hover:text-orange-400 text-xs font-medium"
                            >
                              View
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm space-y-2">
                      <div>
                        <div className="font-medium text-white">
                          Opened: {formatDate(dispute.dispute_created_at)}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {formatTime(dispute.dispute_created_at)}
                        </div>
                      </div>
                      {dispute.updated_at && dispute.updated_at !== dispute.dispute_created_at && (
                        <div>
                          <div className="font-medium text-gray-300">
                            Updated: {formatDate(dispute.updated_at)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(dispute.updated_at)}
                          </div>
                        </div>
                      )}
                      {dispute.dispute_resolved_at && (
                        <div>
                          <div className="font-medium text-green-400">
                            Resolved: {formatDate(dispute.dispute_resolved_at)}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {formatTime(dispute.dispute_resolved_at)}
                          </div>
                        </div>
                      )}
                      {dispute.escalation_level && (
                        <div className="mt-2">
                          <div className="px-2 py-1 bg-yellow-900/30 text-yellow-400 text-xs font-medium rounded inline-flex items-center">
                            <FiShield className="mr-1" />
                            Level {dispute.escalation_level}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-2 min-w-[180px]">
                      <button
                        onClick={() => navigate(`/admin/escrows/${dispute.escrow_id}`)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02]"
                      >
                        <FiEye className="mr-2" />
                        View Details
                      </button>
                      
                      <button
                        onClick={() => {
                          setSelectedDispute(dispute);
                          setResolutionData({
                            resolution: 'release_seller',
                            refund_amount: 0,
                            release_amount: dispute.amount,
                            resolution_notes: ''
                          });
                          setResolutionModal(true);
                        }}
                        className="px-3 py-1.5 bg-[#ff660e]/20 hover:bg-[#ff660e]/30 text-[#ff660e] hover:text-orange-400 rounded-lg text-sm font-medium flex items-center justify-center transition-all duration-200 hover:scale-[1.02] border border-[#ff660e]/30"
                      >
                        <FiGitPullRequest className="mr-2" />
                        Resolve Dispute
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {disputes.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="text-gray-600 text-8xl mb-6">⚖️</div>
            <h3 className="text-xl font-medium text-white mb-2">No active disputes found</h3>
            <p className="text-gray-400 mb-6">All escrow disputes have been resolved.</p>
            <button
              onClick={() => navigate('/admin/escrows')}
              className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium hover:scale-[1.02]"
            >
              View All Escrows
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-400">
                Showing <span className="font-semibold text-white">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-semibold text-white">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-semibold text-white">{pagination.total}</span> disputes
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  <FiArrowLeft className="mr-2" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                          pagination.page === pageNum
                            ? 'bg-[#ff660e] text-white shadow-lg'
                            : 'border border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-gray-700 rounded-lg text-sm font-medium text-gray-400 bg-gray-800 hover:bg-gray-700 hover:text-white hover:border-[#ff660e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-all duration-200 hover:scale-[1.02]"
                >
                  Next
                  <FiArrowRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resolution Modal */}
      {resolutionModal && selectedDispute && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Resolve Dispute #{selectedDispute.dispute_id}
                  </h3>
                  <p className="text-sm text-gray-400 mt-1">
                    Amount: <span className="text-blue-400 font-medium">{formatCurrency(selectedDispute.amount)}</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setResolutionModal(false);
                    setSelectedDispute(null);
                  }}
                  className="text-gray-400 hover:text-white text-xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-5">
                {/* Resolution Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Resolution Type
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <button
                      onClick={() => setResolutionData({
                        ...resolutionData,
                        resolution: 'release_seller',
                        refund_amount: 0,
                        release_amount: selectedDispute.amount
                      })}
                      className={`p-4 rounded-xl border-2 flex flex-col items-center transition-all hover:scale-[1.02] ${
                        resolutionData.resolution === 'release_seller'
                          ? 'border-green-500 bg-green-900/20'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                      }`}
                    >
                      <FiTrendingUp className={`text-2xl mb-2 ${
                        resolutionData.resolution === 'release_seller' ? 'text-green-400' : 'text-green-300'
                      }`} />
                      <span className="font-medium text-white">Release to Seller</span>
                      <span className="text-xs text-gray-400 mt-1">Full amount</span>
                    </button>
                    
                    <button
                      onClick={() => setResolutionData({
                        ...resolutionData,
                        resolution: 'refund_buyer',
                        refund_amount: selectedDispute.amount,
                        release_amount: 0
                      })}
                      className={`p-4 rounded-xl border-2 flex flex-col items-center transition-all hover:scale-[1.02] ${
                        resolutionData.resolution === 'refund_buyer'
                          ? 'border-red-500 bg-red-900/20'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                      }`}
                    >
                      <FiTrendingDown className={`text-2xl mb-2 ${
                        resolutionData.resolution === 'refund_buyer' ? 'text-red-400' : 'text-red-300'
                      }`} />
                      <span className="font-medium text-white">Refund to Buyer</span>
                      <span className="text-xs text-gray-400 mt-1">Full amount</span>
                    </button>
                    
                    <button
                      onClick={() => setResolutionData({
                        ...resolutionData,
                        resolution: 'partial_split',
                        refund_amount: selectedDispute.amount * 0.5,
                        release_amount: selectedDispute.amount * 0.5
                      })}
                      className={`p-4 rounded-xl border-2 flex flex-col items-center transition-all hover:scale-[1.02] ${
                        resolutionData.resolution === 'partial_split'
                          ? 'border-blue-500 bg-blue-900/20'
                          : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                      }`}
                    >
                      <FiPercent className={`text-2xl mb-2 ${
                        resolutionData.resolution === 'partial_split' ? 'text-blue-400' : 'text-blue-300'
                      }`} />
                      <span className="font-medium text-white">Partial Split</span>
                      <span className="text-xs text-gray-400 mt-1">Custom split</span>
                    </button>
                  </div>
                </div>

                {/* Amount Fields for Partial Split */}
                {resolutionData.resolution === 'partial_split' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Refund to Buyer
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">₦</span>
                        <input
                          type="number"
                          value={resolutionData.refund_amount}
                          onChange={(e) => setResolutionData({
                            ...resolutionData,
                            refund_amount: parseFloat(e.target.value) || 0,
                            release_amount: selectedDispute.amount - (parseFloat(e.target.value) || 0)
                          })}
                          className="w-full pl-8 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Release to Seller
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">₦</span>
                        <input
                          type="number"
                          value={resolutionData.release_amount}
                          onChange={(e) => setResolutionData({
                            ...resolutionData,
                            release_amount: parseFloat(e.target.value) || 0,
                            refund_amount: selectedDispute.amount - (parseFloat(e.target.value) || 0)
                          })}
                          className="w-full pl-8 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Resolution Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Resolution Notes
                  </label>
                  <textarea
                    value={resolutionData.resolution_notes}
                    onChange={(e) => setResolutionData({
                      ...resolutionData,
                      resolution_notes: e.target.value
                    })}
                    className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-500"
                    rows="4"
                    placeholder="Explain your decision and any relevant details..."
                  />
                </div>

                {/* Summary */}
                <div className="bg-gray-700/30 rounded-xl p-4 border border-gray-600">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Resolution Summary</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Type:</span>
                      <span className="font-medium text-white">
                        {resolutionData.resolution === 'release_seller' && 'Release to Seller'}
                        {resolutionData.resolution === 'refund_buyer' && 'Refund to Buyer'}
                        {resolutionData.resolution === 'partial_split' && 'Partial Split'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Buyer Receives:</span>
                      <span className="font-medium text-red-400">
                        {formatCurrency(resolutionData.refund_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Seller Receives:</span>
                      <span className="font-medium text-green-400">
                        {formatCurrency(resolutionData.release_amount)}
                      </span>
                    </div>
                    <div className="pt-3 border-t border-gray-600">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-300">Total Amount:</span>
                        <span className="text-lg font-bold text-blue-400">
                          {formatCurrency(selectedDispute.amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setResolutionModal(false);
                  setSelectedDispute(null);
                }}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium hover:scale-[1.02]"
              >
                Cancel
              </button>
              <button
                onClick={handleResolveDispute}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
              >
                <FiCheck className="mr-2" />
                Resolve Dispute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default EscrowDisputes;